// src/sim/hiding/peek.ts — P3 peek held-state + auto-release + narrow cone (HIDE-06, D-21, D-24).
//
// Anti-oscillation contract (BLOCKER-3):
//   Plan 07 stage 12 (checkPeekAutoRelease) sets `peekSuppressedUntilRelease=true`
//   when auto-releasing peek, to prevent the user's still-held finger from
//   immediately re-engaging peek on the next tick's applyPeekIntent (stage 13b).
//   applyPeekIntent MUST:
//     1. Ignore the peek intent while peekSuppressedUntilRelease=true — no state
//        change, no event. This is the "finger stuck down" case.
//     2. Clear peekSuppressedUntilRelease to false on the first tick where the
//        observed peekHeld intent is false — the user has lifted their finger,
//        so a subsequent press is legitimate.
//
// Invariants:
//   - Pure: { next, events } shape mirroring applyHideToggle and the catch stages.
//     No-op paths return the ORIGINAL entity reference so tick.ts can short-circuit.
//   - peek-ended fires on BOTH the manual release path (applyPeekIntent false→hiding)
//     AND the auto-release path (checkPeekAutoRelease) — Plan 07 composes the two
//     stages in a single tick pass.
//   - computePeekConeBitset delegates to computeConeFov with the P3 D-24 cone
//     tunables (22.5° half-angle, 4-tile radius) and converts the entity's
//     world-pixel position to tile space via the supplied tileWidth.
//
// Source: .planning/phases/03-core-sim-primitives/03-CONTEXT.md D-21, D-24.

import type { Entity } from '../../shared/types/world';
import type { WalkabilityGrid } from '../../shared/types/map';
import type { GameEvent } from '../../shared/types/events';
import { computeConeFov } from '../lighting/fov';
import {
  P3_PEEK_CONE_HALF_ANGLE_RAD,
  P3_PEEK_CONE_RANGE_TILES,
  P3_PEEK_MAX_DURATION_TICKS,
} from './constants';

export interface PeekIntentResult {
  next: Entity;
  events: GameEvent[];
}

/**
 * Consume the peek HELD intent. Flow:
 *   - If `peekSuppressedUntilRelease=true`, the peek intent is ignored until the
 *     observed `peekHeld` is false — at which point the flag is cleared (no event).
 *   - Otherwise: hiding + peekHeld=true → peeking (peekEndTick=tick+45, peek-started).
 *   - Otherwise: peeking + peekHeld=false → hiding (peekEndTick=null, peek-ended).
 *   - All other combinations are no-ops.
 */
export function applyPeekIntent(
  entity: Entity,
  peekHeld: boolean,
  currentTick: number,
): PeekIntentResult {
  // Anti-oscillation guard (BLOCKER-3). Observe the release edge to clear
  // the flag; never re-engage peek while the flag is still set.
  if (entity.peekSuppressedUntilRelease) {
    if (!peekHeld) {
      return {
        next: { ...entity, peekSuppressedUntilRelease: false },
        events: [],
      };
    }
    return { next: entity, events: [] };
  }

  if (entity.status === 'hiding' && peekHeld) {
    return {
      next: {
        ...entity,
        status: 'peeking',
        peekEndTick: currentTick + P3_PEEK_MAX_DURATION_TICKS,
      },
      events: [{ type: 'peek-started', entityId: entity.id, tick: currentTick }],
    };
  }

  if (entity.status === 'peeking' && !peekHeld) {
    return {
      next: { ...entity, status: 'hiding', peekEndTick: null },
      events: [{ type: 'peek-ended', entityId: entity.id, tick: currentTick }],
    };
  }

  return { next: entity, events: [] };
}

/**
 * Per-tick check: if the entity is peeking and the 1.5 s window has elapsed,
 * auto-release. Sets `peekSuppressedUntilRelease=true` so the next tick's
 * `applyPeekIntent` cannot immediately re-engage peek when the user's finger
 * is still held on the button (BLOCKER-3).
 */
export function checkPeekAutoRelease(
  entity: Entity,
  currentTick: number,
): PeekIntentResult {
  if (
    entity.status === 'peeking' &&
    entity.peekEndTick !== null &&
    currentTick >= entity.peekEndTick
  ) {
    return {
      next: {
        ...entity,
        status: 'hiding',
        peekEndTick: null,
        peekSuppressedUntilRelease: true,
      },
      events: [{ type: 'peek-ended', entityId: entity.id, tick: currentTick }],
    };
  }
  return { next: entity, events: [] };
}

/**
 * Compute the narrow peek cone (D-24: 45° total / 4-tile radius) into
 * `out`. Delegates to {@link computeConeFov} with the P3 peek tunables.
 * Converts the entity's world-pixel position to tile coordinates via
 * `tileWidth` (no implicit constants — callers that want a different tile
 * size get a different cone).
 */
export function computePeekConeBitset(
  entity: Entity,
  grid: WalkabilityGrid,
  widthInTiles: number,
  tileWidth: number,
  out: Uint32Array,
): void {
  const tileX = entity.position.x / tileWidth;
  const tileY = entity.position.y / tileWidth;
  computeConeFov(
    tileX,
    tileY,
    entity.facing,
    P3_PEEK_CONE_HALF_ANGLE_RAD,
    P3_PEEK_CONE_RANGE_TILES,
    grid,
    widthInTiles,
    out,
  );
}
